import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  Type,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { RbacService } from '../../services/rbac/rbac.service';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from '../../services/unsaved-changes/unsaved-changes-host.helper';
import {
  CanComponentDeactivate,
  confirmDiscardUnsavedChanges,
} from '../../guards/unsaved-changes.guard';
import {
  RolePermissionSummaryCard,
  RbacSummaryResponse,
} from './role-permission-management.models';
@Component({
  selector: 'app-role-permission-management',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet, RouterLink, MatTabsModule],
  templateUrl: './role-permission-management.component.html',
  styleUrl: './role-permission-management.component.scss',
})
export class RolePermissionManagementComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly rbacService = inject(RbacService);
  private readonly dialog = inject(MatDialog);
  private readonly unsavedChanges = inject(UnsavedChangesTrackerService);
  private readonly unsavedHost = new UnsavedChangesHostHelper(
    this.unsavedChanges,
    this.dialog,
    inject(Router)
  );

  /** Controlled tab index so we can block switches when dirty. */
  selectedTabIndex = 0;

  rolesManagementComponent: Type<unknown> | null = null;
  permissionsManagementComponent: Type<unknown> | null = null;
  rolePermissionAssignmentComponent: Type<unknown> | null = null;
  installerRolePermissionAssignmentComponent: Type<unknown> | null = null;

  ngOnInit(): void {
    this.loadSummaryCards();
    this.rbacService.summaryInvalidated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadSummaryCards());
    void import('../roles-management/roles-management.component').then((m) => {
      this.rolesManagementComponent = m.RolesManagementComponent;
      this.cdr.markForCheck();
    });
    void import('../permissions-management/permissions-management.component').then(
      (m) => {
        this.permissionsManagementComponent = m.PermissionsManagementComponent;
        this.cdr.markForCheck();
      }
    );
    void import(
      '../admin-role-permission-assignment/admin-role-permission-assignment.component'
    ).then((m) => {
      this.rolePermissionAssignmentComponent = m.AdminRolePermissionAssignmentComponent;
      this.cdr.markForCheck();
    });
    void import(
      '../installer-role-permission-assignment/installer-role-permission-assignment.component'
    ).then((m) => {
      this.installerRolePermissionAssignmentComponent =
        m.InstallerRolePermissionAssignmentComponent;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.unsavedHost.clearOnDestroy();
  }

  /**
   * Intercept Material tab clicks. MatTabGroup emits after intent to change;
   * keep selectedTabIndex in sync only when leave is allowed.
   */
  async onSelectedTabIndexChange(nextIndex: number): Promise<void> {
    if (nextIndex === this.selectedTabIndex) {
      return;
    }

    if (!this.unsavedChanges.hasUnsavedChanges()) {
      this.selectedTabIndex = nextIndex;
      return;
    }

    const stayOn = this.selectedTabIndex;
    // Keep visual selection on the dirty tab while the confirm dialog is open.
    this.selectedTabIndex = nextIndex;
    this.cdr.detectChanges();
    this.selectedTabIndex = stayOn;
    this.cdr.detectChanges();

    const discard = await confirmDiscardUnsavedChanges(this.dialog);
    if (discard) {
      this.unsavedHost.discard();
      this.selectedTabIndex = nextIndex;
      this.cdr.detectChanges();
    }
  }

  /** Route leave confirmation (Back/Forward / in-app navigation). */
  canDeactivate(): Promise<boolean> {
    return this.unsavedHost.canDeactivate();
  }

  /** Browser Refresh / Close / Reload. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    this.unsavedHost.onBeforeUnload(event);
  }

  summaryCards: RolePermissionSummaryCard[] = [
    { label: 'Total Roles', value: '—' },
    { label: 'Total Permissions', value: '—' },
    { label: 'Assigned Reseller Admin Users', value: '—' },
    { label: 'Assigned Customer Admin Users', value: '—' },
  ];

  /** API / floating-filter values for role deep-links on User List. */
  private static readonly RESELLER_ADMIN_ROLE_FILTER = 'Reseller_Admin';
  private static readonly CUSTOMER_ADMIN_ROLE_FILTER = 'Customer_Admin';

  private loadSummaryCards(): void {
    this.rbacService.getRbacSummary().subscribe({
      next: (summary) => {
        this.summaryCards = this.toSummaryCards(summary);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.warn('Failed to load RBAC summary cards.', error);
        this.cdr.markForCheck();
      },
    });
  }

  private toSummaryCards(summary: RbacSummaryResponse): RolePermissionSummaryCard[] {
    return [
      { label: 'Total Roles', value: String(summary.totalRoles ?? 0) },
      { label: 'Total Permissions', value: String(summary.totalPermissions ?? 0) },
      {
        label: 'Assigned Reseller Admin Users',
        value: String(summary.resellerAdminRoles ?? 0),
        link: '/user-management/user-list',
        queryParams: {
          userRole: RolePermissionManagementComponent.RESELLER_ADMIN_ROLE_FILTER,
        },
      },
      {
        label: 'Assigned Customer Admin Users',
        value: String(summary.customerAdminRoles ?? 0),
        link: '/user-management/user-list',
        queryParams: {
          userRole: RolePermissionManagementComponent.CUSTOMER_ADMIN_ROLE_FILTER,
        },
      },
    ];
  }
}
